/**
 * @module app.cm
 */

import { autoinject, TaskQueue, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { deepClone, sleep } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPrintable } from 'app/shared/lib/printing';
import { validate } from 'app/shared/elements/ess-rule';
import { StoreFocusEvent, ReturnFocusEvent } from 'app/shared/lib/events';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import { AddGoals } from 'app/cm/components/case/care-plan/add-goals';
import { AddMcgGoals } from 'app/cm/components/case/care-plan/add-mcg-goals';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

const expandedGoalsCache = new Map<string, Array<CM.CarePlanGoal>>();
const includeInactiveGoalsCache = new Map<string, boolean>();

@autoinject()
export class CarePlan extends ComponentBase implements IPrintable {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected showRouteLink: boolean = true;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected rememberExpand: boolean = true;
    protected includeInactive: boolean = false;
    protected goals: ReadonlyArray<CM.CarePlanGoal> = [];

    protected selectedGoalForInterventions: CM.CarePlanGoal | null = null;
    protected selectedGoalForBarriers: CM.CarePlanGoal | null = null;
    protected selectedGoalForOutcomes: CM.CarePlanGoal | null = null;
    protected goalOption: string = 'selectGoals';
    protected mcgCopyright?: string;
    protected mcgCustomContentDisclaimer?: string;
    protected barrierOption: string = 'selectBarriers';

    protected goalDetails: Record<string, boolean> = {};

    protected loadGoalModal: boolean = false;
    protected loadMcgGoalModal: boolean = false;
    protected loadBarrierModal: boolean = false;
    protected loadOutcomeModal: boolean = false;
    protected loadInterventionModal: boolean = false;

    protected hasEditGoalRoute: boolean = false;
    protected hasAddGoalPermission: boolean = false;
    protected hasAddInterventionPermission: boolean = false;
    protected hasAddBarrierPermission: boolean = false;
    protected hasAddOutcomePermission: boolean = false;
    /** Permission that controls whether the MCG Informed integration is available */
    protected hasMcgInformed: boolean = false;
    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'cm/careplans',
        targetType: 'CM.CarePlanGoal',
        backRouteName: 'cm.case.careplan',
        taskDetailRouteName: 'cm.case.careplan.interventions.edit',
        addTargetProperty: this.addGoalProperty.bind(this),
        primaryTargetType: 'CM.CarePlanIntervention',
        labelKey: 'cm:carePlanIntervention.$',
        allowDuplicates: false
    };

    protected queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };

    protected validate = validate;
    protected isValid: boolean = false;

    protected caseSettings: IApiSettings = {};
    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CM.CarePlanGoal[]>;
    protected $pager!: EssPagerCustomElement;
    protected $goalModal!: IPopup;
    protected $mcgGoalModal!: IPopup;
    protected $goalForm!: IForm;
    protected $mcgGoalForm!: IForm;
    protected $addGoals!: AddGoals;
    protected $addMcgGoals!: AddMcgGoals;
    protected $barrierModal!: IPopup;
    protected $barrierForm!: IForm;
    protected $outcomeModal!: IPopup;
    protected $outcomeForm!: IForm;
    protected $interventionModal!: IPopup;
    protected $interventionForm!: IForm;
    protected $tasksForm!: Element;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly service: CarePlanService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();

        this.caseSettings = this.context.settings;
        this.tasksConfig.allowDuplicates = !!getApiSetting(this.caseSettings, 'carePlan.goals.interventions.$allowDuplicates');
        this.tasksConfig.memberId = this.context.case?.member?.id || undefined;

        this.reload();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );

        // Set generalized copyright text
        const copyright = getApiSetting(this.caseSettings, 'mcg.informed.copyright.$display')?.toString();
        const edition = getApiSetting(this.caseSettings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);
    }

    protected attached(): void {
        if(this.$tasksForm) {
            this.isValid = validate(this.$tasksForm);
        }
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent): Promise<void> {
        if(e.case && e.case.id === this.caseId) {
            await this.accountContext.load();
            const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
            await this.reload(true);
        };
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected async formSuccess({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('cm:carePlanGoal.$', true);
        this.$goalModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload(true);
    }

    protected async formSuccessMcg({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('ext:cci.goal.$', true);
        this.$mcgGoalModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload(true);
    }

    protected async formSuccessBarrier({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('cm:carePlanBarrier.$', true);
        this.$barrierModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload();
    }

    protected async formSuccessOutcome({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('cm:carePlanOutcome.$', true);
        this.$outcomeModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload();
    }

    protected async formSuccessIntervention({ detail }: FormSuccessCustomEvent): Promise<void> {
        this.toaster.standard.saved('cm:carePlanIntervention.$', true);
        this.$interventionModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        await this.reload();
    }

    protected async handleInactiveSwitchChange(): Promise<void> {
        if(this.caseId) {
            includeInactiveGoalsCache.set(this.caseId, this.includeInactive);
            await this.reload();
        };
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }


    ////****************** Functions ************************************************////
    public async prepareForPrinting(): Promise<void> {
        // expand all goals
        for(let index = 0; index < this.goals.length; index++) {
            this.$table.open(index);
        }
        // this may not be needed, but it won't be noticeable
        // this will make sure all the elements get updated before the call to window.print
        await sleep(250);
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasEditGoalRoute = this.accountContext.hasRouteAccess('cm.case.careplan.goals.edit');
        this.hasMcgInformed = this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment');
        this.hasAddGoalPermission = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.Add');
        this.hasAddInterventionPermission = this.accountContext.hasPermission('CM.CarePlanIntervention.Insert.Add');
        this.hasAddBarrierPermission = this.accountContext.hasPermission('CM.CarePlanBarrier.Insert.Add');
        this.hasAddOutcomePermission = this.accountContext.hasPermission('CM.CarePlanOutcome.Insert.Add');
    }

    public async reload(reloadCurrentGoals: boolean = false): Promise<void> {
        if(!this.caseId) return;
        this.restoreInactiveSwitch();
        await this.loadCarePlan();
        // reload current goals on their respective modals to get the latest (necessary when changing case tabs)
        if(reloadCurrentGoals && this.hasAddGoalPermission) {
            if(this.$addGoals) await this.$addGoals.loadCurrentGoals();
            if(this.$addMcgGoals && this.hasMcgInformed) await this.$addMcgGoals.loadCurrentGoals();
        }
    }

    protected newGoal(): void {
        this.loadGoalModal = true;
        this.$goalModal.open();
    }

    protected newMcgGoal(): void {
        this.loadMcgGoalModal = true;
        this.$mcgGoalModal.open();
    }

    protected goalModalClose(): void {
        if(this.$goalForm.reset) {
            this.$goalForm.reset();
        }
        this.$goalModal.close();
    }

    protected mcgGoalModalClose(): void {
        if(this.$mcgGoalForm.reset) {
            this.$mcgGoalForm.reset();
        }
        this.$mcgGoalModal.close();
    }

    protected newBarrier(goal: CM.CarePlanGoal): void {
        this.loadBarrierModal = true;
        this.selectedGoalForBarriers = deepClone(goal);

        // Set custom content disclaimer based on parsed content
        if(goal.externalSystem?.code?.toLowerCase() === 'cci' && goal.assessmentAttachment?.externalContent?.content) {
            const content = goal.assessmentAttachment.externalContent.content;
            const assessment: Mcg.Assessment = JSON.parse(content);
            this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
        } else {
            this.mcgCustomContentDisclaimer = undefined;
        }

        if(this.$barrierForm.reset) this.$barrierForm.reset();
        this.$barrierModal.open();
    }

    protected barrierModalClose(): void {
        this.$barrierModal.close();
    }

    protected newOutcome(goal: CM.CarePlanGoal): void {
        this.loadOutcomeModal = true;
        this.selectedGoalForOutcomes = deepClone(goal);
        this.$outcomeModal.open();
    }

    protected outcomeModalClose(): void {
        if(this.$outcomeForm.reset) {
            this.$outcomeForm.reset();
        }
        this.$outcomeModal.close();
    }

    protected newIntervention(goal: CM.CarePlanGoal): void {
        this.loadInterventionModal = true;
        this.selectedGoalForInterventions = deepClone(goal);
        this.$interventionModal.open();
    }

    protected interventionModalClose(): void {
        if(this.$interventionForm.reset) {
            this.$interventionForm.reset();
        }
        this.$interventionModal.close();
    }

    protected addGoalProperty(model: CM.CarePlanIntervention): void {
        if(!this.selectedGoalForInterventions || !this.selectedGoalForInterventions.id) { throw new Error('Selected goal id was not set'); }
        model.goal = { id: this.selectedGoalForInterventions.id };
        model.case = { id: this.caseId };
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the goals into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.caseId) { return; }
            const expandedGoals = expandedGoalsCache.get(this.caseId);
            if(expandedGoals !== undefined) {
                this.$table.restoreExpanded(expandedGoals);
            }
        });
    }

    protected goalDetailExpanded({ detail }: ExpandEvent<CM.CarePlanGoal>) {
        const itemOrIndex = detail.item;
        const items = detail.items;
        let foundItem: CM.CarePlanGoal | number | null = null;

        if(itemOrIndex != null) {
            // detail.item returned is a CM.CarePlan Goal
            if(isNaN(parseInt(<string>itemOrIndex))) {
                foundItem = itemOrIndex;
            }
            // detail.item returned is an index coming from restoreExpanded
            else {
                const index: number = <number>itemOrIndex;
                foundItem = this.goals[index];
            }
        }

        if(!!foundItem && items.includes(foundItem)) {
            this.goalDetails[foundItem.id!] = true;
        }

        if(!this.rememberExpand || !this.caseId) { return; }
        expandedGoalsCache.set(this.caseId, detail.items);
    }

    protected restoreInactiveSwitch() {
        if(!this.caseId) { return; }
        const activeSwitchCache = includeInactiveGoalsCache.get(this.caseId);
        if(activeSwitchCache !== undefined) {
            this.includeInactive = activeSwitchCache;
        }
    }

    protected buildCreatedByMessage(goal: CM.CarePlanGoal): string | null {
        const stamp = goal.metadata?.created?.timestamp;
        const byUser = goal.metadata?.created?.byUser?.description;


        if(goal.metadata?.triggeredBy?.description) {
            const key = 'ui:stamps.triggeredBy.timestamp'
            const source = goal.metadata.triggeredBy.description;
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { source: source, timestamp: stamp, user: byUser });
        }
        else {
            const key = 'ui:stamps.created.timestamp'
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { timestamp: stamp, user: byUser });
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCarePlan(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'assessmentAttachment.externalContent',
            'definition',
            'externalSystem',
            'priority',
            'metadata.activeChanged.byUser',
            'metadata.completed.byUser',
            'metadata.created.byUser',
            'metadata.targetDateSet.byUser'
        ];

        const fields = [
            'assessmentAttachment.externalContent.content',
            'assessmentAttachment.externalContent.isContentEncrypted',
            'assessmentAttachment.externalContent.mediaType.code',
            'definition.id',
            'description',
            'externalSystem.code',
            'isCompleted',
            'priority.description',
            'metadata.activeChanged.byUser.name.full',
            'metadata.activeChanged.timestamp',
            'metadata.completed.byUser.name.full',
            'metadata.completed.timestamp',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'metadata.targetDateSet.byUser.name.full',
            'metadata.targetDateSet.timestamp',
            'metadata.triggeredBy.description',
            'targetDate'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseCarePlan(this.caseId, options, includes, fields);
            this.goals = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.queue.queueTask(() => {
                this.events.publish(new ReturnFocusEvent());
            });
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateGoal(goalId: string, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:carePlanGoal.$');
            if(result.dialogResponse === confirmation.options.CANCEL) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCarePlanGoal(goalId, isActive);
            isSuccessful = response.isSuccessful;
            await this.reload(true);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cm:carePlanGoal.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
